import Table from '@/pages/reports/non-ats-reports/shared/table';
import { Box, Icon, Flex, useDisclosure } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { useCallback, useMemo, useState } from 'react';
import { useFlexLayout, useTable, useExpanded, useSortBy } from 'react-table';
import { AiFillCaretDown, AiFillCaretRight } from 'react-icons/ai';
import { groupBy } from '../../../candidate-touchpoint/component/table/flow-health-table';
import { ActivityPopup } from './activity-popup';
import { Title } from '@/pages/reports/non-ats-reports/shared/title';
import { formatMonthString } from '@/pages/reports/custom-reports/transformers/chartTableFlatTransformer';
// import { logicalSort } from '@/pages/reports/non-ats-reports/shared/sort/logicalSort';
import { useKFlags } from '@/hooks/use-k-flags';
import { DrilldownActivityPopup } from '../../../drilldownActivity';
import { fetchDrilldown } from '@/store/reports/flow-health/user-activity/actions';
import {
  selectDrilldownData,
  selectDrilldownDataIsLoading,
  selectCurrentDrillPage,
  selectTotalCount,
  selectSearchTerm,
  selectInitialLoading,
} from '@/store/reports/flow-health/user-activity/selectors';
import { useSelector } from 'react-redux';
import { useFlowHealthDrilldownConfig } from '../../../candidate-touchpoint/component/table/useFlowHealthDrilldownConfig';
import {
  applySearch,
  clearDrilldownData,
} from '@/store/reports/flow-health/user-activity';
import { Table as AtsTable } from '@/components/ats';
import { HeaderCell } from '@/components/ats/Table/tableCells';

export const UserActivityTable = ({
  filters = {},
  data = [],
  isReadOnly = false,
  filterParams = undefined,
  hideLinks = undefined,
  isFullScreen = false,
  tile = {},
  onSortChange = (_sortState) => {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  tableColStyles = {},
  isEditMode = false,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState(null);
  const { feDrilldown } = useKFlags();
  const isDrilldownLoading = useSelector(selectDrilldownDataIsLoading);
  const drilldownData = useSelector(selectDrilldownData);
  const totalCount = useSelector(selectTotalCount);
  const currentDrillPage = useSelector(selectCurrentDrillPage);
  const searchTerm = useSelector(selectSearchTerm);
  const initialLoading = useSelector(selectInitialLoading);

  const onClickRow = useCallback(
    ({ id, row = {} }) => {
      setSelected({ row, id, type: 'table' });
      onOpen();
    },
    [onOpen]
  );

  const { ats_enabled } = useKFlags();

  // Calculate totals for footer
  const calculateTotals = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Use the first row's total values since they should be the same across all rows
    const firstRow = data[0];
    return {
      added: firstRow?.added_total || 0,
      sent_first: firstRow?.sent_first_total || 0,
      sent_followup: firstRow?.sent_followup_total || 0,
      replied: firstRow?.replied_total || 0,
      interested: firstRow?.interested_total || 0,
    };
  }, [data]);

  const title = groupBy?.find((g) => g.id === filters?.group_by);
  const renderMainColumn = useCallback(
    () => ({
      id: 'header',
      accessor: 'group_by',
      title: title?.label || 'Time',
      columnConfig: false,
      Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
        <Flex>
          {filters?.break_down && !isReadOnly && (
            <Icon
              as={isAllRowsExpanded ? AiFillCaretDown : AiFillCaretRight}
              w="16px"
              h="16px"
              {...getToggleAllRowsExpandedProps()}
              onClick={(e) => {
                e.stopPropagation();
                getToggleAllRowsExpandedProps()?.onClick?.(e);
              }}
            />
          )}
          <KText size="sm-semi-bold" textTransform="Capitalize" ml="6px">
            {title?.label || 'Time'}
          </KText>
        </Flex>
      ),
      sort: true,
      Cell: ({ row }) => {
        const rawData = row?.original?.group_by;
        // Format month strings for display when grouping by time
        const data = !filters.group_by ? formatMonthString(rawData) : rawData;
        if (!row.canExpand && row?.depth === 0) {
          return <Title groupBy={data} filters={filters} />;
        }
        if (!row.canExpand && row?.depth === 1) {
          return <Title groupBy={data} filters={filters} pl="24px" />;
        }
        return (
          <Flex
            {...row.getToggleRowExpandedProps({
              style: {
                paddingLeft: `${row.depth * 2}rem`,
              },
            })}
          >
            {row.isExpanded ? (
              <Icon as={AiFillCaretDown} w="16px" h="16px" />
            ) : (
              <Icon as={AiFillCaretRight} w="16px" h="16px" />
            )}
            <Title
              groupBy={data}
              filters={filters}
              ml="6px"
              fontWeight="semiBold"
            />
          </Flex>
        );
      },
      Footer: () => <KText size="sm-semi-bold">Total</KText>,
    }),
    [isReadOnly, filters, title?.label]
  );

  const renderColumn = useCallback(
    (header, key) => ({
      Header: () => {
        return (
          <Flex>
            <HeaderCell title={header} variant="" />
          </Flex>
        );
      },
      accessor: key,
      title: header,
      sort: true,
      width: 150,
      Cell: ({ row }) => {
        const isInvalid = [undefined, null, 0].includes(row?.values?.[key]);
        const value = row?.values?.[key] ?? '-';

        return (
          <KText
            cursor={feDrilldown ? 'pointer' : 'default'}
            _hover={{
              textDecoration: feDrilldown ? 'underline' : 'none',
            }}
            fontWeight={
              row.canExpand && row.depth === 0 && !isReadOnly ? '650' : '550'
            }
            color={ats_enabled || isInvalid ? 'secondary-700' : 'primary-500'}
            textAlign={ats_enabled ? 'start' : undefined}
            onClick={() => {
              if (!isInvalid && !isReadOnly && !hideLinks)
                onClickRow({ id: key, row });
            }}
            size="sm"
          >
            {value}
          </KText>
        );
      },
      Footer: () => {
        const total = calculateTotals[key] || 0;
        return (
          <KText
            size="sm-semi-bold"
            textAlign={ats_enabled ? 'start' : undefined}
          >
            {total}
          </KText>
        );
      },
      columnConfig: true, // This column should be configurable
    }),
    [
      calculateTotals,
      ats_enabled,
      feDrilldown,
      isReadOnly,
      hideLinks,
      onClickRow,
    ]
  );

  const columns = useMemo(() => {
    return [
      renderMainColumn(),
      renderColumn('Added to kula', 'added', 'added_per'),
      renderColumn('1st message sent', 'sent_first', 'sent_first_per'),
      renderColumn('Follow-up sent', 'sent_followup', 'sent_followup_per'),
      renderColumn('Replied', 'replied', 'replied_per'),
      renderColumn('Interested', 'interested', 'interested_per'),
    ];
  }, [filters, renderColumn, renderMainColumn]); // eslint-disable-line

  const table = useTable(
    {
      columns,
      data: data,
      initialState: {
        expanded: { 0: true },
      },
    },
    useFlexLayout,
    useSortBy,
    useExpanded
  );

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  return (
    <Box h="100%">
      {ats_enabled ? (
        <AtsTable
          tableId="user-activity-table"
          isColumnLevelSorting
          enableColumnConfig={isEditMode}
          defaultSortBy={[{ id: 'header', desc: false }]}
          columns={columns}
          data={data}
          enableFooter={true}
          layoutScrollContainerStyles="c-data-table wide-content table-container"
          enableExpanded={true}
          getTableState={handleSortChange}
          onTableColDataChange={onTableColDataChange}
          initialTableColData={initialTableColData}
          skipLocalStorage={skipLocalStorage}
          tableColStyles={tableColStyles}
          isEditMode={isEditMode}
          layoutStyles={{
            borderBottom: 'none',
          }}
        />
      ) : (
        <Table
          table={table}
          isReadOnly={isReadOnly}
          isFullScreen={isFullScreen}
        />
      )}
      {isOpen &&
        (feDrilldown ? (
          <DrilldownActivityPopup
            isOpen={isOpen}
            onClose={onClose}
            filters={filters}
            selected={selected}
            filterParams={filterParams}
            fetchDrilldownAction={fetchDrilldown}
            drilldownData={drilldownData}
            isLoading={isDrilldownLoading}
            totalCount={totalCount}
            currentDrillPage={currentDrillPage}
            resetDrill={clearDrilldownData}
            applySearch={applySearch}
            searchTerm={searchTerm}
            useColumnConfig={useFlowHealthDrilldownConfig}
            initialLoading={initialLoading}
            tile={tile}
          />
        ) : (
          <ActivityPopup
            filters={filters}
            selected={selected}
            isOpen={isOpen}
            onClose={onClose}
            filterParams={filterParams}
          />
        ))}
    </Box>
  );
};
